// RuntimeContext — shared state for the Smart Page Live Preview.
//
// The runtime hosts blocks rendered by the existing designer renderers
// but adds the state the designer canvas does not:
//   1. A page-vars store (input values + selection variables).
//   2. A save dispatcher (`DocSync`) that routes block property edits
//      to the backend in document mode.
//   3. Reorganize-mode state — per-scope ordering overrides, tombstones
//      for template blocks, runtime-added block index, picker + drag
//      state — all mirrored to a synthetic `__doc_state__` block so
//      the layout survives reload.
//
// Runtime mode is one of:
//   - 'preview'  → in-memory only (internal / external pages); no doc
//                  CRUD, no persistence.
//   - 'document' → wired to a smart document instance; property edits
//                  PATCH to /api/documents/{id}/blocks/{blockId}.
//
// Scope keys mirror the Angular runtime so the persisted `order` map
// is portable between runtimes:
//   - section root        →  `<sec.id>` (e.g. `sec_main`)
//   - root (no sections)  →  `__root__`
//   - accordion item slot →  `<accordion.id>/items/<item.id>`
//   - tabs item slot      →  `<tabs.id>/items/<item.id>`
//   - columns column slot →  `<columns.id>/columns/<col.id>`
//   - container/card body →  `<block.id>/children`

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { APP_BASE } from '@/config/basePath'
import type { ReactNode } from 'react'
import type {
  Block, BlockType, PageModel, PageDataSource, ResolvedDataEntry, RuntimeDeletePolicy,
} from '@/pages/designers/page/types'
import type { DocumentBlock } from '@/api/documents'
import { usePageApiVariables } from '@/pages/designers/page/pageApiVariables'
import { listUserMemory } from '@/api/userMemory'
import { listProjectVariables } from '@/api/projectVariables'
import { buildProjectVarSeed } from '@twasta/page-runtime/userMemory'
import { substituteVars } from '@/pages/designers/page/actions/substitute'
import { DocSync, type SyncState } from './docSync'
import { useYjsDocument } from './useYjsDocument'
import { type DataAccess, fetchDataSource as fetchDS, seedResources } from './dataBinding'
import { RECORDS_CHANGED_EVENT } from '@twasta/page-runtime/commerceApi'
import { AuthMenuContext } from '@twasta/page-runtime/renderers'
import { useLiveSources } from '@twasta/page-runtime/live/liveSources'
import type { LiveRowsState } from '@twasta/page-runtime/live/repeatFrame'

export type RuntimeMode = 'preview' | 'document'

// The synthetic block id the document uses to persist per-document
// state (vars, order, deleted). The doc-runtime row never renders.
export const DOC_STATE_BLOCK_ID = '__doc_state__'
export const ROOT_SCOPE = '__root__'

export interface RuntimePolicies {
  runtimeAddable: boolean
  runtimeAddableTypes?: BlockType[]
  runtimeDeletable: RuntimeDeletePolicy
}

// ─── Picker catalogue ───────────────────────────────────────
// Mirrors the Angular runtime's RUNTIME_PICKER_OPTIONS. Each entry
// supplies the BlockType, display data, and a `seed()` factory for
// initial `properties` so newly inserted blocks render meaningfully
// (e.g. a heading shows up at all, a todo has an empty first item).

export type PickerCategory = 'Basic' | 'Layout' | 'Content' | 'Media' | 'Action' | 'Form' | 'App'

export interface PickerOption {
  type: BlockType
  label: string
  icon: string
  description: string
  category: PickerCategory
  seed: () => Record<string, unknown>
}

function randId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID().slice(0, 8)
  return Math.random().toString(36).slice(2, 10)
}

const emptySeed = (): Record<string, unknown> => ({})

export const RUNTIME_PICKER_OPTIONS: PickerOption[] = [
  // Basic
  { type: 'paragraph',     label: 'Text',          icon: '¶',   description: 'Plain paragraph',                  category: 'Basic',   seed: () => ({ text: '' }) },
  { type: 'heading',       label: 'Heading',       icon: 'H',   description: 'Section heading (H1/H2/H3)',       category: 'Basic',   seed: () => ({ text: '', level: 2 }) },
  { type: 'bullet_list',   label: 'Bulleted list', icon: '•',   description: 'Unordered list',                   category: 'Basic',   seed: () => ({ items: [{ id: 'li_' + randId(), text: '' }] }) },
  { type: 'numbered_list', label: 'Numbered list', icon: '1.',  description: 'Ordered list',                     category: 'Basic',   seed: () => ({ items: [{ id: 'li_' + randId(), text: '' }] }) },
  { type: 'todo',          label: 'To-do',         icon: '☐',   description: 'Checkbox item list',               category: 'Basic',   seed: () => ({ items: [{ id: 'td_' + randId(), text: '', checked: false }] }) },
  { type: 'quote',         label: 'Quote',         icon: '❝',   description: 'Block quote',                      category: 'Basic',   seed: () => ({ text: '' }) },
  { type: 'code',          label: 'Code',          icon: '</>', description: 'Monospace code block',             category: 'Basic',   seed: () => ({ text: '', language: 'plaintext' }) },
  { type: 'divider',       label: 'Divider',       icon: '—',   description: 'Horizontal divider',               category: 'Basic',   seed: emptySeed },
  { type: 'callout',       label: 'Callout',       icon: '💡',  description: 'Highlighted info box',             category: 'Basic',   seed: () => ({ variant: 'info', body: '' }) },
  { type: 'notepad',       label: 'Notepad',       icon: '📝',  description: 'Rich-text notepad',                category: 'Basic',   seed: () => ({ text: '' }) },
  { type: 'rich_content',  label: 'Rich content',  icon: '📰',  description: 'A Rich Text column from your data', category: 'Basic',  seed: () => ({ source: '{{item.body}}', toc: 'auto', typography: 'article', readableWidth: true, externalLinksNewTab: true }) },

  // Layout
  { type: 'hero',          label: 'Hero',          icon: '🎯',  description: 'Big banner with title + CTAs',     category: 'Layout',  seed: () => ({ title: '', subtitle: '', align: 'center', background: { kind: 'none' } }) },
  { type: 'cta_banner',    label: 'CTA banner',    icon: '📣',  description: 'Call-to-action banner',            category: 'Layout',  seed: () => ({ title: '', subtitle: '', align: 'center', background: { kind: 'none' } }) },
  { type: 'columns',       label: 'Columns',       icon: '▥',   description: 'Multi-column layout',              category: 'Layout',  seed: () => ({ columns: [{ id: 'col_' + randId(), blocks: [] }, { id: 'col_' + randId(), blocks: [] }] }) },
  { type: 'container',     label: 'Container',     icon: '▢',   description: 'Colored section container',        category: 'Layout',  seed: () => ({ children: [] }) },
  { type: 'spacer',        label: 'Spacer',        icon: '↕',   description: 'Vertical spacing',                 category: 'Layout',  seed: () => ({ height: 24 }) },
  { type: 'card',          label: 'Card',          icon: '🃏',  description: 'Single content card',              category: 'Layout',  seed: () => ({ children: [] }) },
  { type: 'card_grid',     label: 'Card grid',     icon: '▦',   description: 'Grid of cards',                    category: 'Layout',  seed: () => ({ cards: [] }) },

  // Content
  { type: 'feature_grid',  label: 'Feature grid',  icon: '✨',  description: 'Icon + title + description grid',  category: 'Content', seed: () => ({ items: [] }) },
  { type: 'stats',         label: 'Stats',         icon: '#',   description: 'Number + label statistics',        category: 'Content', seed: () => ({ items: [] }) },
  { type: 'testimonial',   label: 'Testimonial',   icon: '❝',   description: 'Customer quote',                   category: 'Content', seed: () => ({ quote: '', author: '' }) },
  { type: 'pricing',       label: 'Pricing table', icon: '$',   description: 'Pricing tiers with CTAs',          category: 'Content', seed: () => ({ tiers: [] }) },
  { type: 'faq',           label: 'FAQ',           icon: '❓',  description: 'Question/answer accordion',        category: 'Content', seed: () => ({ items: [] }) },
  { type: 'tabs',          label: 'Tabs',          icon: '⊟',   description: 'Tabbed content',                   category: 'Content', seed: () => ({ items: [{ id: 'tab_' + randId(), label: 'Tab 1', blocks: [] }] }) },
  { type: 'accordion',     label: 'Accordion',     icon: '☰',   description: 'Collapsible sections',             category: 'Content', seed: () => ({ items: [{ id: 'acc_' + randId(), title: '', blocks: [] }] }) },
  { type: 'timeline',      label: 'Timeline',      icon: '◷',   description: 'Vertical timeline',                category: 'Content', seed: () => ({ items: [] }) },
  { type: 'logo_cloud',    label: 'Logo cloud',    icon: '⬢',   description: 'Logo strip',                       category: 'Content', seed: () => ({ items: [] }) },
  { type: 'table',         label: 'Table',         icon: '⊞',   description: 'Simple data table',                category: 'Content', seed: () => ({ columns: [], rows: [] }) },

  // Media
  { type: 'image',         label: 'Image',         icon: '🖼',  description: 'Image from URL',                   category: 'Media',   seed: () => ({ url: '' }) },
  { type: 'video',         label: 'Video',         icon: '▶',   description: 'YouTube / Vimeo / file URL',       category: 'Media',   seed: () => ({ url: '' }) },
  { type: 'gallery',       label: 'Gallery',       icon: '🗂',  description: 'Image gallery',                    category: 'Media',   seed: () => ({ items: [] }) },
  { type: 'map',           label: 'Map',           icon: '📍',  description: 'Interactive map',                  category: 'Media',   seed: () => ({ provider: 'leaflet', locationMode: 'address', address: '', zoom: 14, height: 400 }) },

  // Action
  { type: 'button',        label: 'Button',        icon: '▣',   description: 'Single CTA button',                category: 'Action',  seed: () => ({ button: { id: 'btn_' + randId(), label: 'Click', variant: 'primary', on_click: { kind: 'none' } }, align: 'left' }) },
  { type: 'button_group',  label: 'Button group',  icon: '▥',   description: 'Row of buttons',                   category: 'Action',  seed: () => ({ buttons: [], align: 'left' }) },

  // Form
  { type: 'newsletter',    label: 'Newsletter',    icon: '✉',   description: 'Email signup',                     category: 'Form',    seed: emptySeed },
  { type: 'contact_form',  label: 'Contact form',  icon: '📝',  description: 'Name / email / message',           category: 'Form',    seed: () => ({ fields: [] }) },
  { type: 'form',          label: 'Form',          icon: '🗒',  description: 'Generic form with fields',         category: 'Form',    seed: () => ({ fields: [] }) },

  // App
  { type: 'input_group',   label: 'Inputs',        icon: '⌨',   description: 'Render page input controls',       category: 'App',     seed: () => ({ inputs: [], layout: 'stacked' }) },
  { type: 'embed',         label: 'Embed object',  icon: '⊞',   description: 'Embed a transaction / dashboard',  category: 'App',     seed: () => ({ embed: { obj_type: 'T', obj_name: '', mode: 'list' } }) },
  { type: 'custom_code',   label: 'Custom code',   icon: '⌘',   description: 'Sandboxed HTML / CSS / JS',        category: 'App',     seed: () => ({ html: '', css: '', js: '' }) },
  { type: 'variable',      label: 'Variable',      icon: '𝑥',   description: 'Display a page variable',          category: 'App',     seed: () => ({ name: '' }) },
]

export const PICKER_CATEGORY_ORDER: PickerCategory[] = [
  'Basic', 'Layout', 'Content', 'Media', 'Action', 'Form', 'App',
]

// ─── Public API ─────────────────────────────────────────────

export interface PickerAnchor {
  left: number
  top: number
  placeAbove: boolean
}

export interface PickerContext {
  scope: string
  afterBlockId: string | null
  /**
   * Ordered ids currently rendered in this scope, captured at the
   * moment the picker was opened. Used to splice the new block into
   * `docOrder` at the right position when the scope has no override
   * yet (template blocks aren't DB siblings, so the server can't
   * place the row; placement lives entirely client-side).
   */
  currentOrder: string[]
}

export interface DragSource {
  scope: string
  blockId: string
}

export interface DragTarget {
  scope: string
  blockId: string
  position: 'before' | 'after'
}

export interface RuntimeApi {
  mode: RuntimeMode
  docId: string | null
  page: PageModel
  project: string
  /** How data is resolved for this runtime (preview / public / site). Embeds
   *  read this to route transaction model + CRUD calls through the matching
   *  endpoint — site mode uses the site-auth /site-txn path so embedded forms
   *  work for end-users in the public preview. */
  dataAccess: DataAccess
  /** Re-fetch the page model to obtain fresh page-bound embed tokens (external
   *  site pages). Returns a map of block id → token, or null if unavailable.
   *  Embeds call this when a site-txn call 401s with EMBED_TOKEN_EXPIRED. */
  refreshEmbedTokens?: () => Promise<Record<string, string> | null>
  /** True in the in-app designer preview, where no deployment data is baked
   *  into the page — so even `deployment`-bound sources must be fetched live
   *  (matching the old Angular preview). False on the deployed/public runtime,
   *  where deployment sources arrive baked in `page.__resolved_data__`. */
  isPreview: boolean
  /** Current viewer's roles (for visibility gating). undefined = unknown. */
  userRoles?: string[]

  // Page vars (substitution + selection variables).
  vars: Record<string, unknown>
  setVar: (name: string, value: unknown) => void
  setVars: (patch: Record<string, unknown>) => void

  // Transaction data-binding results. Keyed `ds:<dataSourceId>`. Seeded
  // from baked `page.__resolved_data__` and from runtime fetches.
  resources: Record<string, unknown>
  /** Fetch a data source live (runtime binding). `args` defaults to the
   *  current page-input values; `scopes` carries enclosing rows for
   *  correlated (nested) sources. Page-scope results (no scopes) are
   *  merged into `resources`. */
  fetchDataSource: (
    source: PageDataSource,
    args?: Record<string, unknown>,
    scopes?: Record<string, unknown>,
  ) => Promise<ResolvedDataEntry>
  /** Re-read data sources now (`undefined` = every auto-fetched page source).
   *  A failed read keeps what the page already shows. Also bumps
   *  `refreshTick`, which nested (correlated) repeats re-read on. */
  refreshSources: (ids?: string[]) => Promise<void>
  /** Increments on every `refreshSources` / live poll. */
  refreshTick: number
  /** Arrivals on live (`refresh`) sources — read by the shared RepeatFrame. */
  liveRows: LiveRowsState
  /** The page runtime registers how to run a page action, for `onNewRows.action`. */
  setLiveActionRunner: (fn: ((name: string, extra: Record<string, unknown>) => void) | null) => void

  // Per-doc block state (document mode). Keyed by block id.
  blockProps: Record<string, Record<string, unknown>>
  /** Block ids that exist only on the document instance. */
  runtimeAddedIds: Set<string>
  /** Full DocumentBlock records for runtime-added blocks, keyed by id. */
  addedBlocks: Record<string, DocumentBlock>
  /**
   * Return added blocks whose parent_id matches `scope`, sorted by
   * server-assigned position. Used by renderers to merge runtime adds
   * into the rendered tree alongside template blocks.
   */
  childrenAddedUnder: (scope: string | null) => DocumentBlock[]

  // Mutations
  saveProperties: (blockId: string, properties: Record<string, unknown>) => void
  addBlock: (input: {
    type: BlockType
    parentId?: string | null
    afterBlockId?: string | null
    properties?: Record<string, unknown>
    /**
     * Ordered ids currently rendered under `parentId`. When supplied,
     * `docOrder[scope]` is seeded with this list (with the new block
     * spliced in after `afterBlockId`) so the new block lands at the
     * requested position even when no override existed before.
     */
    currentOrder?: string[]
  }) => Promise<void>
  removeBlock: (blockId: string) => Promise<void>

  // Reorganize-mode state
  reorganizeMode: boolean
  setReorganizeMode: (next: boolean) => void
  /** Scope → ordered id list. Overrides natural order when present. */
  docOrder: Record<string, string[]>
  /** Tombstoned block ids (template or added). Hidden from render. */
  deletedIds: Set<string>
  /** Apply a move within a scope. */
  moveBlock: (scope: string, srcId: string, targetId: string, position: 'before' | 'after') => void
  /** Move a block N positions within a scope (used by up/down buttons). */
  moveBlockBy: (scope: string, id: string, dir: 'up' | 'down') => void
  /** Tombstone a block (added rows also fire REST delete via removeBlock). */
  tombstoneBlock: (id: string) => void

  // Picker
  pickerContext: PickerContext | null
  pickerAnchor: PickerAnchor | null
  pickerSearch: string
  openPicker: (
    scope: string,
    afterBlockId: string | null,
    currentOrder: string[],
    ev?: { currentTarget: EventTarget | null },
  ) => void
  closePicker: () => void
  setPickerSearch: (q: string) => void

  // Drag-drop
  dragSource: DragSource | null
  dragOverTarget: DragTarget | null
  startDrag: (scope: string, blockId: string) => void
  setDragOver: (scope: string, blockId: string, position: 'before' | 'after') => void
  clearDragOver: (scope: string, blockId: string) => void
  endDrag: (scope: string, targetBlockId: string) => void
  cancelDrag: () => void

  // Policy predicates
  canEdit: (block: Block) => boolean
  canDelete: (block: Block) => boolean
  canAddInto: (parent: Block | null) => boolean

  // Sync state surfaced from DocSync.
  syncState: SyncState
  flushSaves: () => Promise<void>
}

const NOOP_API: RuntimeApi = {
  mode: 'preview',
  docId: null,
  page: { obj_name: '', title: '', inputs: [], blocks: [] },
  project: '',
  dataAccess: { mode: 'preview', project: '' },
  isPreview: true,
  userRoles: undefined,
  vars: {},
  setVar: () => {},
  setVars: () => {},
  resources: {},
  fetchDataSource: async () => ({ mode: 'list', rows: [], total: 0 }),
  refreshSources: async () => {},
  refreshTick: 0,
  liveRows: { newKeys: {}, highlightSeconds: {} },
  setLiveActionRunner: () => {},
  blockProps: {},
  runtimeAddedIds: new Set(),
  addedBlocks: {},
  childrenAddedUnder: () => [],
  saveProperties: () => {},
  addBlock: async () => {},
  removeBlock: async () => {},
  reorganizeMode: false,
  setReorganizeMode: () => {},
  docOrder: {},
  deletedIds: new Set(),
  moveBlock: () => {},
  moveBlockBy: () => {},
  tombstoneBlock: () => {},
  pickerContext: null,
  pickerAnchor: null,
  pickerSearch: '',
  openPicker: () => {},
  closePicker: () => {},
  setPickerSearch: () => {},
  dragSource: null,
  dragOverTarget: null,
  startDrag: () => {},
  setDragOver: () => {},
  clearDragOver: () => {},
  endDrag: () => {},
  cancelDrag: () => {},
  canEdit: () => false,
  canDelete: () => false,
  canAddInto: () => false,
  syncState: { pending: false, lastSavedAt: null, lastError: null },
  flushSaves: async () => {},
}

const Ctx = createContext<RuntimeApi>(NOOP_API)
export function useRuntime(): RuntimeApi { return useContext(Ctx) }

// ─── Provider ───────────────────────────────────────────────

export interface RuntimeProviderProps {
  mode: RuntimeMode
  page: PageModel
  docId: string | null
  project: string
  initialBlockProps?: Record<string, Record<string, unknown>>
  initialAddedIds?: Iterable<string>
  initialAddedBlocks?: Record<string, DocumentBlock>
  initialVars?: Record<string, unknown>
  /** Persisted scope→order overrides from `__doc_state__.order`. */
  initialOrder?: Record<string, string[]>
  /** Persisted tombstone ids from `__doc_state__.deleted`. */
  initialDeleted?: Iterable<string>
  /** Current viewer's roles, threaded to visibility gating. */
  userRoles?: string[]
  /** Transport for runtime data-source fetches. Defaults to authenticated
   *  in-app preview against `project`; deployed sites pass `public`. */
  dataAccess?: DataAccess
  /** See RuntimeApi.refreshEmbedTokens — supplied by the site viewer. */
  refreshEmbedTokens?: () => Promise<Record<string, string> | null>
  /**
   * Opt-in live collaboration. When true (and in document mode), block-
   * property edits sync in real time via the shared Yjs gateway instead of
   * REST `DocSync`. Off by default — the REST path is unchanged unless this
   * is explicitly enabled. Structure (order/added/deleted) stays on REST.
   */
  collab?: boolean
  children: ReactNode
}

const DOC_STATE_DEBOUNCE_MS = 600

export function RuntimeProvider({
  mode,
  page,
  docId,
  project,
  initialBlockProps,
  initialAddedIds,
  initialAddedBlocks,
  initialVars,
  initialOrder,
  initialDeleted,
  collab = false,
  userRoles,
  dataAccess,
  refreshEmbedTokens,
  children,
}: RuntimeProviderProps) {
  const [vars, setVarsState] = useState<Record<string, unknown>>(() => ({ ...(initialVars || {}) }))
  // Data-binding resources, seeded from baked deployment-time data.
  const [resources, setResources] = useState<Record<string, unknown>>(
    () => seedResources(page.__resolved_data__),
  )
  const [blockProps, setBlockProps] = useState<Record<string, Record<string, unknown>>>(
    () => ({ ...(initialBlockProps || {}) }),
  )
  const [runtimeAddedIds, setRuntimeAddedIds] = useState<Set<string>>(
    () => new Set(initialAddedIds ? Array.from(initialAddedIds) : []),
  )
  const [addedBlocks, setAddedBlocks] = useState<Record<string, DocumentBlock>>(
    () => ({ ...(initialAddedBlocks || {}) }),
  )
  const [docOrder, setDocOrder] = useState<Record<string, string[]>>(
    () => ({ ...(initialOrder || {}) }),
  )
  const [deletedIds, setDeletedIds] = useState<Set<string>>(
    () => new Set(initialDeleted ? Array.from(initialDeleted) : []),
  )

  const syncRef = useRef<DocSync | null>(null)
  const [syncState, setSyncState] = useState<SyncState>({ pending: false, lastSavedAt: null, lastError: null })
  const [reorganizeMode, setReorganizeMode] = useState(false)

  // ─── Live collaboration (Yjs) — opt-in via `collab` ───
  // Block-property edits sync through the shared Yjs gateway (the same one
  // the Angular runtime uses); structure stays on REST, matching Angular's
  // hybrid model. The hook runs every render (null docId when disabled) so
  // hook order stays stable; it only opens a socket when enabled.
  const collabEnabled = collab && mode === 'document' && !!docId
  const {
    connected: collabConnected,
    blockProps: yjsBlockProps,
    setBlockProperties: yjsSetBlockProperties,
  } = useYjsDocument(collabEnabled ? docId : null, { enabled: collabEnabled })

  // When collab is live, the Y.Doc is authoritative for block properties;
  // overlay it on the REST-seeded props (which provide instant content
  // before the first sync lands). Otherwise the REST props are used as-is.
  const effectiveBlockProps = useMemo(
    () => (collabEnabled && collabConnected ? { ...blockProps, ...yjsBlockProps } : blockProps),
    [collabEnabled, collabConnected, blockProps, yjsBlockProps],
  )

  // Picker state — `pickerContext` null means closed.
  const [pickerContext, setPickerContext] = useState<PickerContext | null>(null)
  const [pickerAnchor, setPickerAnchor] = useState<PickerAnchor | null>(null)
  const [pickerSearch, setPickerSearchState] = useState('')

  // Drag-drop state.
  const [dragSource, setDragSource] = useState<DragSource | null>(null)
  const [dragOverTarget, setDragOverTarget] = useState<DragTarget | null>(null)

  useEffect(() => {
    if (mode === 'document' && docId) {
      const ds = new DocSync(docId)
      syncRef.current = ds
      const unsub = ds.subscribe(setSyncState)
      return () => {
        unsub()
        ds.flush()
        if (syncRef.current === ds) syncRef.current = null
      }
    }
    syncRef.current = null
    setSyncState({ pending: false, lastSavedAt: null, lastError: null })
    return undefined
  }, [mode, docId])

  // ─── Doc-state flush helper (defined after state, used by flushSaves) ─
  // We split the actual dispatch into a ref-bound function so both the
  // debounce timer and the explicit `flushSaves` path share one
  // implementation without re-creating a closure on every render.
  const docStateDispatchRef = useRef<(() => Promise<void>) | null>(null)

  const flushSaves = useCallback(async () => {
    // Drain any pending doc-state save first so an in-flight reorder
    // lands on the server before block-property writes finish flushing.
    if (docStateTimerRef.current) {
      clearTimeout(docStateTimerRef.current)
      docStateTimerRef.current = null
      await docStateDispatchRef.current?.()
    }
    await syncRef.current?.flush()
  }, [])

  // ─── Vars ──────────────────────────────────────────
  const setVar = useCallback((name: string, value: unknown) => {
    setVarsState((prev) => (prev[name] === value ? prev : { ...prev, [name]: value }))
  }, [])
  const setVars = useCallback((patch: Record<string, unknown>) => {
    setVarsState((prev) => ({ ...prev, ...patch }))
  }, [])

  // ─── Data binding ──────────────────────────────────────
  const access = useMemo<DataAccess>(
    () => dataAccess || { mode: 'preview', project },
    [dataAccess, project],
  )
  // In the in-app preview nothing is baked, so deployment sources must be
  // fetched live just like runtime ones. On the deployed/public runtime,
  // deployment sources arrive baked and only `runtime` sources are fetched.
  const isPreview = access.mode === 'preview'
  const varsRef = useRef(vars)
  varsRef.current = vars

  const fetchDataSource = useCallback<RuntimeApi['fetchDataSource']>(
    async (source, args, scopes) => {
      const entry = await fetchDS(access, source, args ?? varsRef.current, scopes)
      // Page-scope results (no enclosing row) are merged into resources;
      // correlated/scoped results are managed locally by the repeater.
      if (!scopes || Object.keys(scopes).length === 0) {
        setResources((prev) => ({ ...prev, [`ds:${source.id}`]: entry }))
      }
      return entry
    },
    [access],
  )

  // Re-seed baked resources when the page (and its baked data) changes.
  useEffect(() => {
    setResources(seedResources(page.__resolved_data__))
  }, [page.__resolved_data__])

  // Auto-fetch page-scope sources (non-correlated). On the deployed runtime
  // only `runtime` sources are fetched (deployment ones are baked); in the
  // in-app preview nothing is baked, so deployment sources are fetched too.
  // Correlated sources are always fetched per-row by the repeater.
  const shouldAutoFetch = useCallback(
    (s: PageDataSource) =>
      !_isCorrelated(s) && (s.bindingTime === 'runtime' || isPreview),
    [isPreview],
  )
  const runtimeSourcesKey = useMemo(() => {
    const rs = (page.dataSources || []).filter(shouldAutoFetch)
    return JSON.stringify(rs.map((s) => s.id))
  }, [page.dataSources, shouldAutoFetch])

  // `signedInOnly` sources wait for a session (and re-run when one starts).
  const authStatus = useContext(AuthMenuContext)?.status
  const signedIn = authStatus === 'authenticated'

  useEffect(() => {
    const rs = (page.dataSources || []).filter(shouldAutoFetch)
    let cancelled = false
    for (const s of rs) {
      if (s.signedInOnly && !signedIn) {
        setResources((prev) => ({ ...prev, [`ds:${s.id}`]: { mode: s.mode, row: null, rows: [], total: 0 } }))
        continue
      }
      // Skip if already baked/seeded for this source.
      if (resources[`ds:${s.id}`] && !s.signedInOnly) continue
      void (async () => {
        const entry = await fetchDS(access, s, varsRef.current)
        if (!cancelled) setResources((prev) => ({ ...prev, [`ds:${s.id}`]: entry }))
      })()
    }
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtimeSourcesKey, access, signedIn])

  // A block wrote the visitor's records (`saved_records`): re-read every
  // auto-fetched page source, so conditions and text bound to them — here and
  // in the site header's own runtime — show the new state with no reload.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const onChanged = () => {
      for (const s of (page.dataSources || []).filter(shouldAutoFetch)) {
        if (s.signedInOnly && !signedIn) continue
        void fetchDS(access, s, varsRef.current)
          .then((entry) => setResources((prev) => ({ ...prev, [`ds:${s.id}`]: entry })))
          .catch(() => { /* keep the last answer */ })
      }
    }
    window.addEventListener(RECORDS_CHANGED_EVENT, onChanged)
    return () => window.removeEventListener(RECORDS_CHANGED_EVENT, onChanged)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runtimeSourcesKey, access, signedIn])

  // ─── Live refresh (`dataSources[].refresh`, `refresh_source` step) ───
  // One re-read path for the poll, the action steps and anything else: a page
  // source is re-read and REPLACED only on success (a network blip must not
  // empty an operational screen), and `refreshTick` moves so nested
  // (correlated) repeats — whose rows live in the repeater — re-read too.
  const [refreshTick, setRefreshTick] = useState(0)
  const refetchSource = useCallback(async (id: string) => {
    const s = (page.dataSources || []).find((d) => d.id === id)
    if (!s) return
    if (_isCorrelated(s)) { setRefreshTick((t) => t + 1); return }
    if (s.signedInOnly && !signedIn) return
    const entry = await fetchDS(access, s, varsRef.current, undefined, { throwOnError: true })
    setResources((prev) => ({ ...prev, [`ds:${s.id}`]: entry }))
    setRefreshTick((t) => t + 1)
  }, [page.dataSources, access, signedIn])
  const refreshSources = useCallback(async (ids?: string[]) => {
    const list = ids && ids.length
      ? ids
      : (page.dataSources || []).filter((s) => shouldAutoFetch(s) || _isCorrelated(s)).map((s) => s.id)
    await Promise.all(list.map((id) => refetchSource(id).catch(() => { /* keep the last answer */ })))
  }, [page.dataSources, shouldAutoFetch, refetchSource])
  // The page action runner for `onNewRows.action` — registered by the page
  // runtime (it owns the actions), so arrivals can run one.
  const liveActionRef = useRef<((name: string, extra: Record<string, unknown>) => void) | null>(null)
  const setLiveActionRunner = useCallback((fn: ((name: string, extra: Record<string, unknown>) => void) | null) => {
    liveActionRef.current = fn
  }, [])
  const liveRows = useLiveSources({
    sources: page.dataSources,
    resources,
    refetch: refetchSource,
    runAction: useCallback((name: string, extra: Record<string, unknown>) => { liveActionRef.current?.(name, extra) }, []),
    enabled: !reorganizeMode,
    include: useCallback((s: PageDataSource) => !_isCorrelated(s), []),
  })

  usePageApiVariables(
    page.inputs || [], vars, setVar, undefined,
    useCallback((apiName: string) =>
      `${APP_BASE}/api/integration/invoke/${encodeURIComponent(project)}/${encodeURIComponent(apiName)}`,
    [project]),
  )

  // ── Project variables usable directly in the page (no API call) ──
  //
  // On load we resolve the project's variables and seed them as page vars so
  // blocks, bindings, embed args/filters and `{{token}}` text can reference
  // them by name — without authoring a json `api` input:
  //   vars[name]           → the resolved VALUE
  //                           • prompt  : the logged-in user's selected value
  //                                       (or its default)
  //                           • value   : the variable's static value
  //   vars[name + '__label'] → the human-readable LABEL
  //                           • prompt/select : the matching option's col2
  //                                             (e.g. the company NAME)
  //                           • otherwise      : the value itself
  // A page-input default written as the token ":name" is resolved to that
  // variable's value too, so an input can be seeded from a project variable
  // directly. `sql`-kind variables are left untouched (resolved server-side
  // by the data engine). Best-effort: any fetch failure is swallowed so the
  // page still renders (bindings fall back to their literal text).
  //
  // Re-run only when the project changes or a `:token` default is
  // added/changed — not on every unrelated page edit (the designer mutates
  // `page` immutably on each keystroke).
  const colonDefaultsSig = useMemo(
    () => (page.inputs || [])
      .filter((i) => typeof i.default === 'string' && i.default.startsWith(':'))
      .map((i) => `${i.name}=${i.default}`)
      .join('|'),
    [page.inputs],
  )
  useEffect(() => {
    if (!project) return
    let cancelled = false
    void (async () => {
      // Static (`value`-kind) + prompt (per-user) variables, fetched in
      // parallel. `sql`-kind is dropped (resolved server-side by the engine).
      const [valueVars, promptVars] = await Promise.all([
        listProjectVariables(project)
          .then((vs) => vs.filter((v) => v.kind === 'value').map((v) => ({ name: v.name, value: v.value })))
          .catch(() => []),
        listUserMemory(project, 'internal').catch(() => []),
      ])
      if (cancelled) return
      const updates = buildProjectVarSeed({ valueVars, promptVars, inputs: page.inputs || [] })
      if (Object.keys(updates).length) setVars(updates)
    })()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, colonDefaultsSig])

  useEffect(() => {
    const definitions = page.inputs || []
    const updates: Record<string, unknown> = {}
    for (const v of definitions) {
      if (!v.expression) continue
      try {
        const subbed = substituteVars(v.expression, vars)
        let next: unknown
        if (/^[\s0-9"'.\-+*/%<>=!&|()[\]_a-zA-Z,]+$/.test(subbed)) {
          // eslint-disable-next-line @typescript-eslint/no-implied-eval, no-new-func
          next = new Function(`return (${subbed});`)()
        } else {
          next = subbed
        }
        if (vars[v.name] !== next) updates[v.name] = next
      } catch {
        /* leave existing value alone */
      }
    }
    if (Object.keys(updates).length > 0) {
      setVarsState((prev) => ({ ...prev, ...updates }))
    }
  }, [page.inputs, vars])

  // ─── Block property saves ──────────────────────────
  const saveProperties = useCallback(
    (blockId: string, properties: Record<string, unknown>) => {
      setBlockProps((prev) => ({
        ...prev,
        [blockId]: { ...(prev[blockId] || {}), ...properties },
      }))
      if (collabEnabled && collabConnected) {
        // Collab path: write to the shared Y.Doc. The gateway CRDT-merges,
        // broadcasts to peers, and materialises to the same table DocSync
        // writes — so persistence is equivalent, just real-time.
        yjsSetBlockProperties(blockId, properties)
        return
      }
      if (mode === 'document' && syncRef.current) {
        const next = { ...(blockProps[blockId] || {}), ...properties }
        syncRef.current.patch(blockId, next).catch(() => {
          /* error already toasted in DocSync */
        })
      }
    },
    [mode, blockProps, collabEnabled, collabConnected, yjsSetBlockProperties],
  )

  const addBlock = useCallback<RuntimeApi['addBlock']>(
    async ({ type, parentId, afterBlockId, properties, currentOrder }) => {
      if (mode !== 'document' || !syncRef.current) return
      // Note: we deliberately don't pass `after_block_id` to the server.
      // Template blocks aren't DB siblings (they live in the page JSON,
      // not in custom_document_blocks), so the server can't find them
      // among siblings — 404. Order is governed entirely by `docOrder`
      // on the client; the server simply appends.
      const block = await syncRef.current.insert({
        type,
        parent_id: parentId ?? null,
        properties: properties ?? {},
      })
      setBlockProps((prev) => ({ ...prev, [block.id]: block.properties }))
      setRuntimeAddedIds((prev) => {
        const next = new Set(prev)
        next.add(block.id)
        return next
      })
      setAddedBlocks((prev) => ({ ...prev, [block.id]: block }))
      // Splice the new block into the per-scope order. When the scope
      // has no override yet we seed it from `currentOrder` (the entry
      // ids visible at the moment the picker was opened) so the new
      // block lands at the requested position rather than at the end.
      const scopeKey = parentId ?? ROOT_SCOPE
      setDocOrder((prev) => {
        const base = (prev[scopeKey] ?? currentOrder ?? []).filter((id) => id !== block.id)
        const insertAt = afterBlockId == null
          ? 0 // openPicker(null, ...) means "insert at top"
          : (() => {
              const idx = base.indexOf(afterBlockId)
              return idx === -1 ? base.length : idx + 1
            })()
        const nextIds = [...base.slice(0, insertAt), block.id, ...base.slice(insertAt)]
        return { ...prev, [scopeKey]: nextIds }
      })
    },
    [mode],
  )

  const removeBlock = useCallback<RuntimeApi['removeBlock']>(
    async (blockId) => {
      if (mode !== 'document' || !syncRef.current) return
      await syncRef.current.remove(blockId)
      setBlockProps((prev) => {
        if (!(blockId in prev)) return prev
        const next = { ...prev }
        delete next[blockId]
        return next
      })
      setRuntimeAddedIds((prev) => {
        if (!prev.has(blockId)) return prev
        const next = new Set(prev)
        next.delete(blockId)
        return next
      })
      setAddedBlocks((prev) => {
        if (!(blockId in prev)) return prev
        const next = { ...prev }
        delete next[blockId]
        return next
      })
      // Drop the deleted id from any scope's order list so it doesn't
      // pin a non-existent block after reload.
      setDocOrder((prev) => {
        const next: Record<string, string[]> = {}
        let changed = false
        for (const [scope, ids] of Object.entries(prev)) {
          const filtered = ids.filter((x) => x !== blockId)
          if (filtered.length !== ids.length) changed = true
          if (filtered.length > 0) next[scope] = filtered
        }
        return changed ? next : prev
      })
    },
    [mode],
  )

  const childrenAddedUnder = useCallback(
    (scope: string | null) => {
      const target = scope ?? null
      const rows = Object.values(addedBlocks).filter((b) => b.parent_id === target)
      rows.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
      return rows
    },
    [addedBlocks],
  )

  // ─── Reorder + tombstone ──────────────────────────
  const moveBlock = useCallback<RuntimeApi['moveBlock']>(
    (scope, srcId, targetId, position) => {
      if (srcId === targetId) return
      setDocOrder((prev) => {
        const baseIds = prev[scope]
        if (!baseIds) return prev
        let fromIdx = baseIds.indexOf(srcId)
        if (fromIdx === -1) return prev
        let toIdx = baseIds.indexOf(targetId)
        if (toIdx === -1) return prev
        if (position === 'after') toIdx += 1
        const next = [...baseIds]
        next.splice(fromIdx, 1)
        if (fromIdx < toIdx) toIdx -= 1
        next.splice(toIdx, 0, srcId)
        return { ...prev, [scope]: next }
      })
    },
    [],
  )

  const moveBlockBy = useCallback<RuntimeApi['moveBlockBy']>(
    (scope, id, dir) => {
      setDocOrder((prev) => {
        const baseIds = prev[scope]
        if (!baseIds) return prev
        const idx = baseIds.indexOf(id)
        if (idx === -1) return prev
        const target = dir === 'up' ? idx - 1 : idx + 1
        if (target < 0 || target >= baseIds.length) return prev
        const next = [...baseIds]
        ;[next[idx], next[target]] = [next[target], next[idx]]
        return { ...prev, [scope]: next }
      })
    },
    [],
  )

  const tombstoneBlock = useCallback((id: string) => {
    setDeletedIds((prev) => {
      if (prev.has(id)) return prev
      const next = new Set(prev)
      next.add(id)
      return next
    })
    setDocOrder((prev) => {
      const next: Record<string, string[]> = {}
      let changed = false
      for (const [scope, ids] of Object.entries(prev)) {
        const filtered = ids.filter((x) => x !== id)
        if (filtered.length !== ids.length) changed = true
        if (filtered.length > 0) next[scope] = filtered
      }
      return changed ? next : prev
    })
  }, [])

  // ─── Picker ──────────────────────────────────────────
  const openPicker = useCallback<RuntimeApi['openPicker']>((scope, afterBlockId, currentOrder, ev) => {
    setPickerContext({ scope, afterBlockId, currentOrder })
    setPickerSearchState('')
    setPickerAnchor(computeAnchor(ev?.currentTarget))
  }, [])

  const closePicker = useCallback(() => {
    setPickerContext(null)
    setPickerAnchor(null)
    setPickerSearchState('')
  }, [])

  const setPickerSearch = useCallback((q: string) => {
    setPickerSearchState(q)
  }, [])

  // ─── Drag-drop ──────────────────────────────────────
  const startDrag = useCallback((scope: string, blockId: string) => {
    setDragSource({ scope, blockId })
  }, [])

  const setDragOver = useCallback((scope: string, blockId: string, position: 'before' | 'after') => {
    setDragOverTarget((cur) => {
      if (cur && cur.scope === scope && cur.blockId === blockId && cur.position === position) return cur
      return { scope, blockId, position }
    })
  }, [])

  const clearDragOver = useCallback((scope: string, blockId: string) => {
    setDragOverTarget((cur) => {
      if (cur && cur.scope === scope && cur.blockId === blockId) return null
      return cur
    })
  }, [])

  const endDrag = useCallback<RuntimeApi['endDrag']>(
    (scope, targetBlockId) => {
      const src = dragSource
      const target = dragOverTarget
      setDragSource(null)
      setDragOverTarget(null)
      if (!src || src.scope !== scope || src.blockId === targetBlockId) return
      moveBlock(scope, src.blockId, targetBlockId, target?.position ?? 'after')
    },
    [dragSource, dragOverTarget, moveBlock],
  )

  const cancelDrag = useCallback(() => {
    setDragSource(null)
    setDragOverTarget(null)
  }, [])

  // ─── Policies ────────────────────────────────────────
  const policies: RuntimePolicies = useMemo(
    () => ({
      runtimeAddable: page.runtimeAddable !== false && page.kind === 'document',
      runtimeAddableTypes: page.runtimeAddableTypes,
      runtimeDeletable: (page.runtimeDeletable ?? 'added_only') as RuntimeDeletePolicy,
    }),
    [page.runtimeAddable, page.runtimeAddableTypes, page.runtimeDeletable, page.kind],
  )

  // Runtime-added blocks are always editable — the customer just put
  // them there, so they own that content regardless of the template
  // `documentEditable` flag (which only governs template blocks).
  const canEdit = useCallback(
    (block: Block) => {
      if (mode !== 'document') return false
      if (runtimeAddedIds.has(block.id)) return true
      return !!block.documentEditable
    },
    [mode, runtimeAddedIds],
  )

  const canDelete = useCallback(
    (block: Block) => {
      if (mode !== 'document') return false
      if (!reorganizeMode) return false
      const policy = policies.runtimeDeletable
      if (policy === 'none') return false
      if (policy === 'any') return true
      if (runtimeAddedIds.has(block.id)) return true
      if (policy === 'added_only') return false
      if (policy === 'marked') return !!block.runtimeDeletable
      if (policy === 'empty') {
        const props = blockProps[block.id] ?? {}
        const text = (props.text ?? (block as { text?: string }).text ?? '') as string
        return !text || !text.trim()
      }
      return false
    },
    [mode, policies.runtimeDeletable, runtimeAddedIds, blockProps, reorganizeMode],
  )

  const canAddInto = useCallback(
    (parent: Block | null) => {
      if (mode !== 'document' || !policies.runtimeAddable) return false
      if (!reorganizeMode) return false
      if (parent === null) return true
      return !!parent.runtimeAddable
    },
    [mode, policies.runtimeAddable, reorganizeMode],
  )

  // ─── __doc_state__ autosave ────────────────────────
  // Persist docOrder + deletedIds (and vars) into the synthetic doc-
  // state row whenever they change. Debounced — multiple drags within
  // a half-second coalesce into one save. The initial snapshot we
  // hydrated from is treated as already-saved, so the first effect
  // run on mount doesn't echo straight back to the server.
  const lastSavedDocStateRef = useRef<string>(
    JSON.stringify(buildDocStatePayload(vars, initialOrder || {}, initialDeleted ? Array.from(initialDeleted) : [])),
  )
  const docStateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (mode !== 'document' || !syncRef.current) return
    const payload = buildDocStatePayload(vars, docOrder, Array.from(deletedIds))
    const serialised = JSON.stringify(payload)
    if (serialised === lastSavedDocStateRef.current) return
    lastSavedDocStateRef.current = serialised
    if (docStateTimerRef.current) clearTimeout(docStateTimerRef.current)
    const sync = syncRef.current
    // Bind a fresh dispatcher so flushSaves can drain the same payload
    // synchronously if the user clicks Save before the debounce fires.
    docStateDispatchRef.current = async () => {
      try {
        await sync.saveDocState(payload)
      } catch {
        // Failure already toasted; invalidate cache so a future
        // change retries instead of skipping as a no-op.
        lastSavedDocStateRef.current = ''
      }
    }
    docStateTimerRef.current = setTimeout(() => {
      docStateTimerRef.current = null
      const fn = docStateDispatchRef.current
      docStateDispatchRef.current = null
      void fn?.()
    }, DOC_STATE_DEBOUNCE_MS)
    return undefined
  }, [mode, vars, docOrder, deletedIds])

  // Flush (don't drop) any pending doc-state save on unmount. The vars/order/
  // deleted save is debounced ~600ms; if the user makes a change and navigates
  // away (e.g. closes the doc or clicks "All documents") within that window,
  // simply clearing the timer would silently lose the change. A common case:
  // uploading a file writes its URL into a page variable, then the user leaves
  // immediately — without this flush the variable (and the image) is gone on
  // reopen. The dispatcher captured its own DocSync + payload, so firing it
  // here (fire-and-forget) sends the save even as DocSync tears down.
  useEffect(() => {
    return () => {
      if (docStateTimerRef.current) {
        clearTimeout(docStateTimerRef.current)
        docStateTimerRef.current = null
        const fn = docStateDispatchRef.current
        docStateDispatchRef.current = null
        void fn?.()
      }
    }
  }, [])

  // Leaving reorganize mode drops any stale drag/picker state so a
  // stray drop or reopened picker can't fire after the toggle.
  useEffect(() => {
    if (!reorganizeMode) {
      setPickerContext(null)
      setPickerAnchor(null)
      setPickerSearchState('')
      setDragSource(null)
      setDragOverTarget(null)
    }
  }, [reorganizeMode])

  const api = useMemo<RuntimeApi>(
    () => ({
      mode,
      docId,
      page,
      project,
      dataAccess: access,
      refreshEmbedTokens,
      isPreview,
      userRoles,
      vars,
      setVar,
      setVars,
      resources,
      fetchDataSource,
      refreshSources,
      refreshTick,
      liveRows,
      setLiveActionRunner,
      blockProps: effectiveBlockProps,
      runtimeAddedIds,
      addedBlocks,
      childrenAddedUnder,
      saveProperties,
      addBlock,
      removeBlock,
      reorganizeMode,
      setReorganizeMode,
      docOrder,
      deletedIds,
      moveBlock,
      moveBlockBy,
      tombstoneBlock,
      pickerContext,
      pickerAnchor,
      pickerSearch,
      openPicker,
      closePicker,
      setPickerSearch,
      dragSource,
      dragOverTarget,
      startDrag,
      setDragOver,
      clearDragOver,
      endDrag,
      cancelDrag,
      canEdit,
      canDelete,
      canAddInto,
      syncState,
      flushSaves,
    }),
    [mode, docId, page, project, access, refreshEmbedTokens, isPreview, userRoles, vars, setVar, setVars, resources, fetchDataSource,
      refreshSources, refreshTick, liveRows, setLiveActionRunner, effectiveBlockProps, runtimeAddedIds,
      addedBlocks, childrenAddedUnder, saveProperties, addBlock, removeBlock,
      reorganizeMode, docOrder, deletedIds, moveBlock, moveBlockBy, tombstoneBlock,
      pickerContext, pickerAnchor, pickerSearch, openPicker, closePicker, setPickerSearch,
      dragSource, dragOverTarget, startDrag, setDragOver, clearDragOver, endDrag, cancelDrag,
      canEdit, canDelete, canAddInto, syncState, flushSaves],
  )

  return <Ctx.Provider value={api}>{children}</Ctx.Provider>
}

// ─── Helpers ────────────────────────────────────────────────

/** True if a data source's filters reference an enclosing repeater row
 *  (item_field) — i.e. it is correlated/nested and resolved per parent row. */
function _isCorrelated(s: PageDataSource): boolean {
  const refs = [
    ...(s.filters || []).flatMap((f) => [f.value, f.from, f.to]),
    s.record && 'value' in s.record ? s.record.value : undefined,
  ]
  return refs.some((r) => r && r.source === 'item_field')
}

const PICKER_EST_HEIGHT = 480
const PICKER_EST_WIDTH = 460

function computeAnchor(target: EventTarget | null | undefined): PickerAnchor {
  const el = target instanceof HTMLElement ? target : null
  const vw = typeof window === 'undefined' ? 1024 : window.innerWidth
  const vh = typeof window === 'undefined' ? 768 : window.innerHeight
  if (!el) {
    return { left: Math.max(8, (vw - PICKER_EST_WIDTH) / 2), top: 60, placeAbove: false }
  }
  const r = el.getBoundingClientRect()
  const spaceBelow = vh - r.bottom
  const placeAbove = spaceBelow < PICKER_EST_HEIGHT && r.top > spaceBelow
  const top = placeAbove
    ? Math.max(8, r.top - PICKER_EST_HEIGHT - 6)
    : r.bottom + 6
  let left = r.left
  if (left + PICKER_EST_WIDTH > vw - 8) {
    left = Math.max(8, vw - PICKER_EST_WIDTH - 8)
  }
  return { left, top, placeAbove }
}

function buildDocStatePayload(
  vars: Record<string, unknown>,
  order: Record<string, string[]>,
  deleted: string[],
): { vars: Record<string, unknown>; order: Record<string, string[]>; deleted: string[] } {
  // Sort the deleted list for a stable serialisation so the
  // string-diff guard doesn't fire on equivalent-but-shuffled sets.
  return { vars, order, deleted: [...deleted].sort() }
}
